// Student Service Demographics — editable form for scheduling-relevant fields.
// Source of truth for the Schedule Wizard. IEP/MET dates use ISO YYYY-MM-DD so
// they can later sync with the Frame IEP/MET product.
//
// Iter 23: Service Obligations rebuilt around minute-based primary obligation
// (minutes_per_week | minutes_per_month) with a delivery sub-pattern
// (sessions_per_period × minutes_per_session). Backward compatible with the
// legacy {kind:"weekly_sessions"|"monthly_minutes", value, duration_min}
// shape — old docs are normalized into the new form when loaded.
import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FloppyDisk, Plus, Trash, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { nativeOptions } from "@/lib/nativeOptions";
import EligibilityEditor, { primaryEligibilityKey } from "@/components/student/EligibilityEditor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
// FP3-072 — Contextual Help Center links.
import HelpLink from "@/components/help/HelpLink";
// FP3-092 — SHARED student-profile taxonomy (same module as the Admin
// Profile tab). Labels + canonical keys + legacy alias normalization.
import {
  ATTENDANCE_OPTIONS,
  SERVICE_STATUS_OPTIONS as SHARED_SERVICE_STATUS_OPTIONS,
  SEX_OPTIONS,
  normalizeAttendancePattern,
  normalizeSex,
} from "@/lib/studentProfileTaxonomy";
// FP3-097 — SHARED teacher name/email helpers (same module as the
// Admin Profile tab): email→name suggestion + surname↔email match.
import {
  deriveTeacherDisplay,
  teacherEmailMatches,
  TEACHER_MISMATCH_MESSAGE,
} from "@/lib/teacherIdentity";

// FP3-070 follow-up (Jun 8, 2026) — Service Status / Therapy Status.
// Canonical user-facing value list. `archived` is a virtual UI option
// that flips `is_archived=true` on save; everything else maps to the
// underlying `status` field.
//   - "active" → IEP (the historical default key)
//   - "rti" → RTI
//   - "monitoring" → Monitoring (watchlist, no scheduling)
//   - "graduated" → Graduated (terminal, no scheduling)
//   - "archived" → is_archived=true (terminal flag, no scheduling)
// "evaluation" and "exited" exist in legacy data but are NOT
// surfaced in the dropdown to keep the UX tight (still accepted by
// PATCH for any other code path that needs them).
const SERVICE_STATUS_OPTIONS = [
  ...SHARED_SERVICE_STATUS_OPTIONS,
  { value: "archived", label: "Archived" },
];
const ACTIVE_THERAPY_UI_VALUES = new Set(["active", "rti"]);
function toServiceStatusUi(student) {
  if (student?.is_archived) return "archived";
  const raw = (student?.status || "active").toLowerCase();
  if (["active", "rti", "monitoring", "graduated"].includes(raw)) return raw;
  // Legacy "evaluation" / "exited" → closest UX bucket = Monitoring.
  if (raw === "evaluation" || raw === "exited") return "monitoring";
  return "active";
}
// FP3-035B correction — Kindergarten = "0" platform-wide. Stored as
// "0", displayed as "0" in selects + headers + sort.
const GRADE_OPTIONS = Array.from({ length: 13 }, (_, i) => String(i));
const KIND_OPTIONS = [
  { value: "minutes_per_week", label: "Minutes / week" },
  { value: "minutes_per_month", label: "Minutes / month" },
  // Iter 27 — explicit "no direct service minutes" option (consult, monitoring, etc.)
  { value: "no_direct_service", label: "No direct service minutes" },
];
// Iter 27 — service-type options now driven by provider role.
// FP3-029C terminology unification (Feb 15, 2026) — "Special Education
// Teacher" is the canonical user-facing role label. The legacy
// "Resource Teacher" key is kept here as a silent alias so any
// in-flight data with the old role string still resolves to the
// expected option list.
const SPED_TEACHER_SERVICES = [
  "reading fluency", "reading comprehension", "math fluency", "operations",
  "written expression", "consult",
];
const ROLE_SERVICE_TYPES = {
  SLP: ["articulation", "language", "pragmatics", "fluency", "voice", "consult"],
  OT: ["fine motor", "sensory", "handwriting", "self-help", "visual motor", "consult"],
  PT: ["gross motor", "balance", "mobility", "strength", "consult"],
  "Special Education Teacher": SPED_TEACHER_SERVICES,
  "Resource Teacher": SPED_TEACHER_SERVICES,
  "School Psychologist": ["assessment", "counseling", "behavior", "social-emotional", "consult"],
  BCBA: ["behavior", "social-emotional", "communication", "self-help", "consult"],
  // FP3-030C — Behavior Specialist as a peer to BCBA. Same option set.
  "Behavior Specialist": ["behavior", "social-emotional", "communication", "self-help", "consult"],
};
const FALLBACK_SERVICE_TYPES = ["language", "OT", "social-emotional", "behavior", "consult"];

function serviceTypesFor(role) {
  if (role && ROLE_SERVICE_TYPES[role]) return ROLE_SERVICE_TYPES[role];
  return FALLBACK_SERVICE_TYPES;
}

// FP3-097 — deriveTeacherDisplay moved to the shared
// `@/lib/teacherIdentity` module (imported above) so Provider and
// Admin surfaces can never derive different suggestions.

function normalizeObligation(o) {
  if (!o) return o;
  // FP3-035B-1 follow-up — Build the multi-area `service_types` list
  // from either the new field or the legacy single `service_type`.
  const legacyType = (o.service_type || "").trim();
  const rawTypes = Array.isArray(o.service_types) ? o.service_types : [];
  const types = [];
  for (const t of rawTypes) {
    const v = (t || "").trim();
    if (v && !types.includes(v)) types.push(v);
  }
  if (types.length === 0 && legacyType) types.push(legacyType);
  const firstType = types[0] || legacyType || "";
  // Iter 27 — explicit "no direct service" obligation
  if (o.kind === "no_direct_service") {
    return {
      kind: "no_direct_service",
      total_minutes: 0,
      service_type: firstType || "consult",
      service_types: types.length ? types : (firstType ? [firstType] : ["consult"]),
      delivery: { sessions_per_period: 0, minutes_per_session: 0 },
    };
  }
  // Legacy shapes
  if (o.kind === "weekly_sessions") {
    const value = parseInt(o.value, 10) || 0;
    const dur = parseInt(o.duration_min, 10) || 0;
    return {
      kind: "minutes_per_week",
      total_minutes: value * dur,
      service_type: firstType,
      service_types: types,
      delivery: { sessions_per_period: value, minutes_per_session: dur },
    };
  }
  if (o.kind === "monthly_minutes") {
    const total = parseInt(o.value, 10) || 0;
    const dur = parseInt(o.duration_min, 10) || 30;
    const sessions = Math.max(1, Math.round(total / Math.max(1, dur)));
    return {
      kind: "minutes_per_month",
      total_minutes: total,
      service_type: firstType,
      service_types: types,
      delivery: { sessions_per_period: sessions, minutes_per_session: dur },
    };
  }
  // New shape (or partial)
  const total = parseInt(o.total_minutes, 10) || 0;
  const minutes_per_session = parseInt(o.delivery?.minutes_per_session, 10) || 30;
  const sessions_per_period =
    parseInt(o.delivery?.sessions_per_period, 10) ||
    Math.max(1, Math.round(total / Math.max(1, minutes_per_session)));
  return {
    kind: o.kind === "minutes_per_month" ? "minutes_per_month" : "minutes_per_week",
    total_minutes: total,
    service_type: firstType,
    service_types: types,
    delivery: { sessions_per_period, minutes_per_session },
  };
}

export default function ServiceDemographics({ student, onSaved }) {
  const [draft, setDraft] = useState(toFormState(student));
  const [saving, setSaving] = useState(false);
  // Iter 27 — service-type options come from the current provider's role.
  const [providerRole, setProviderRole] = useState("");
  useEffect(() => {
    api.getCurrentProvider().then((r) => setProviderRole(r.data?.role || "")).catch(() => {});
  }, []);
  const serviceTypes = serviceTypesFor(providerRole);

  useEffect(() => {
    setDraft(toFormState(student));
  }, [student?.id, student?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  // FP3-035B-1 — Pull eligibility taxonomy for the Eligibility
  // Category select. Stable across the lifetime of the form; failure
  // is silent (the select shows an empty list and the field still
  // saves the raw string).
  const [eligibilityCategories, setEligibilityCategories] = useState([]);
  useEffect(() => {
    api.getEligibilityCategories()
      .then((r) => setEligibilityCategories(r.data?.categories || []))
      .catch(() => setEligibilityCategories([]));
  }, []);
  // FP3-035B-1 follow-up — LRE taxonomy.
  const [lreLevels, setLreLevels] = useState([]);
  useEffect(() => {
    api.getLreLevels()
      .then((r) => setLreLevels(r.data?.levels || []))
      .catch(() => setLreLevels([]));
  }, []);
  // FP3-064 — Schools list for the canonical School/Campus picker.
  // We use `/api/schools` (org-scoped, excludes archived by default
  // via the FP3-063 follow-up) so providers can only assign to a
  // school they themselves can see — same source of truth used by
  // the Caseload filter + Schedule school selector. The picker is
  // gated to admin family + solo OWNER in the JSX below; everyone
  // else gets a read-only display.
  const [schoolOptions, setSchoolOptions] = useState([]);
  useEffect(() => {
    api.listSchools()
      .then((r) => setSchoolOptions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSchoolOptions([]));
  }, []);
  // FP3-064 — Capability gate mirrors the backend rule for
  // student.school_id edits: admin family OR solo provider owner.
  // Backend silently strips school_id from the update if denied —
  // we keep the affordance hidden so users don't see a save toast
  // for a no-op.
  const { user, isSoloWorkspaceOwner, preferences } = useAuth();
  // FP3-075D — Simple Mode hides advanced configuration boxes on
  // Service Demographics. The data + endpoints are untouched; only
  // the FE conditional render is gated. Power users in Advanced
  // Mode see no change.
  const isSimpleMode = preferences?.ui_mode === "simple";
  const isFidelityOff = preferences?.fidelity_monitoring === "off";
  // FP3-079C — Solo workspace owners keep this affordance even when
  // their discipline is SLPA/COTA/PTA (provider_role_type flag says
  // "assistant" for them). Only invited assistants with a
  // supervising provider remain excluded — `isSoloWorkspaceOwner`
  // encodes exactly that rule.
  const canEditSchool = (() => {
    const role = (user?.permission_role || "").toLowerCase();
    if (["super_admin", "admin", "org_admin"].includes(role)) return true;
    if (role !== "provider") return false;
    return !!isSoloWorkspaceOwner;
  })();

  const dirty = JSON.stringify(draft) !== JSON.stringify(toFormState(student));

  const update = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const addObligation = () =>
    setDraft((d) => ({
      ...d,
      service_obligations: [
        ...d.service_obligations,
        {
          kind: "minutes_per_week",
          total_minutes: 60,
          service_type: "",
          // FP3-035B-1 follow-up — Empty multi-area list; provider
          // picks the focus areas covered by this commitment.
          service_types: [],
          delivery: { sessions_per_period: 2, minutes_per_session: 30 },
        },
      ],
    }));

  const removeObligation = (idx) =>
    setDraft((d) => ({
      ...d,
      service_obligations: d.service_obligations.filter((_, i) => i !== idx),
    }));

  const updateObligation = (idx, partial) =>
    setDraft((d) => ({
      ...d,
      service_obligations: d.service_obligations.map((o, i) => {
        if (i !== idx) return o;
        const next = { ...o, ...partial };
        // If user changed delivery, recompute total_minutes for clarity.
        if (partial.delivery) {
          const { sessions_per_period, minutes_per_session } = next.delivery;
          next.total_minutes =
            (parseInt(sessions_per_period, 10) || 0) * (parseInt(minutes_per_session, 10) || 0);
        }
        return next;
      }),
    }));

  // FP3-097 — Build the full PATCH payload from a form state. save()
  // diffs draft vs the initial state and submits ONLY the fields the
  // user actually changed (stale-write safety: editing a service
  // commitment never resends an old teacher / teacher_email pair
  // that Admin may have updated meanwhile).
  const buildPayload = (d) => {
    // FP3-070 follow-up — Map the `service_status` UI value back to
    // the underlying `{status, is_archived}` shape. "archived" is a
    // virtual UI option that flips the is_archived flag without
    // mutating the status field.
    const uiVal = (d.service_status || "active").toLowerCase();
    const statusFields = uiVal === "archived"
      ? { is_archived: true }
      : { status: uiVal, is_archived: false };
    return {
      ...statusFields,
      teacher: d.teacher,
      teacher_email: d.teacher_email,
      grade: d.grade,
      attendance_pattern: d.attendance_pattern,
      date_of_birth: d.date_of_birth || null,
      sex: d.sex || "",
      eligibility_category: primaryEligibilityKey(d.eligibility_categories),
      eligibility_categories: (d.eligibility_categories || []).filter((e) => e.category),
      lre: d.lre || "",
      service_obligations: d.service_obligations.map((o) => ({
        kind: o.kind,
        total_minutes: parseInt(o.total_minutes, 10) || 0,
        service_type: o.service_type || "",
        service_types: o.service_types || [],
        delivery: {
          sessions_per_period: parseInt(o.delivery?.sessions_per_period, 10) || 0,
          minutes_per_session: parseInt(o.delivery?.minutes_per_session, 10) || 0,
        },
      })),
      iep_due_date: d.iep_due_date || null,
      met_due_date: d.met_due_date || null,
      external_id: (d.external_id || "").trim(),
      student_email: (d.student_email || "").trim(),
      school_id: (d.school_id || "").trim(),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const base = buildPayload(toFormState(student));
      const next = buildPayload(draft);
      // FP3-097 — partial PATCH: only fields whose value actually
      // changed relative to the loaded student are submitted.
      const payload = {};
      for (const k of Object.keys(next)) {
        if (JSON.stringify(next[k]) !== JSON.stringify(base[k])) payload[k] = next[k];
      }
      if (Object.keys(payload).length === 0) {
        toast.message("No changes to save");
        return;
      }
      await api.updateStudentDemographics(student.id, payload);
      toast.success("Service Demographics saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
      setPendingExitWarning(false);
    }
  };

  // FP3-070 follow-up — When the user moves an Active-Therapy student
  // (IEP / RTI) to Monitoring / Graduated / Archived, show a one-time
  // confirmation toast warning that any existing schedule blocks may
  // need review/removal. We DON'T auto-delete data. Save is gated on
  // the user's "Continue" — they can also Cancel and reset the
  // dropdown to its prior value.
  const [pendingExitWarning, setPendingExitWarning] = useState(false);
  // FP3-097 — Teacher name ↔ email mismatch confirmation ("Save
  // anyway"). Only gates saves that actually CHANGE the pair, so a
  // legacy mismatched record never blocks unrelated edits.
  const [pendingTeacherMismatch, setPendingTeacherMismatch] = useState(false);
  const initialForm = toFormState(student);
  const teacherPairMismatch =
    !!((draft.teacher || "").trim() && (draft.teacher_email || "").trim()) &&
    !teacherEmailMatches(draft.teacher, draft.teacher_email);
  const teacherPairChanged =
    (draft.teacher || "") !== (initialForm.teacher || "") ||
    (draft.teacher_email || "") !== (initialForm.teacher_email || "");
  const needsExitWarning = () => {
    const initialStatus = toServiceStatusUi(student);
    const wasActiveTherapy = ACTIVE_THERAPY_UI_VALUES.has(initialStatus);
    const willExitActive = !ACTIVE_THERAPY_UI_VALUES.has(draft.service_status);
    return wasActiveTherapy && willExitActive;
  };
  const continueSaveAfterMismatch = () => {
    setPendingTeacherMismatch(false);
    if (needsExitWarning()) {
      setPendingExitWarning(true);
      return;
    }
    save();
  };
  const handleSaveClick = () => {
    if (teacherPairMismatch && teacherPairChanged) {
      setPendingTeacherMismatch(true);
      return;
    }
    if (needsExitWarning()) {
      setPendingExitWarning(true);
      return;
    }
    save();
  };

  return (
    <div className="space-y-5" data-testid="service-demographics">
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
            Scheduling Profile
          </CardTitle>
          <Button
            size="sm"
            onClick={handleSaveClick}
            disabled={!dirty || saving}
            data-testid="demographics-save"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-8 text-xs disabled:opacity-50"
          >
            <FloppyDisk size={12} weight="bold" className="mr-1" />
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </CardHeader>
        {/* FP3-035B-1 follow-up — Scheduling Profile now focuses on
            scheduling-relevant fields only. Eligibility / LRE / IEP &
            MET dates live in the Special Education Profile card below
            so the multi-eligibility editor has room to breathe. */}
        <CardContent className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Grade">
            <select
              value={draft.grade}
              onChange={(e) => update("grade", e.target.value)}
              data-testid="demographics-grade"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              {nativeOptions(GRADE_OPTIONS, (g) => ({ value: g, label: g }))}
            </select>
          </Field>
          {/* FP3-064 — Canonical School/Campus assignment. Source of
              truth is `school_id`; the legacy `campus` display string
              is synced server-side. Only admin family + solo OWNER
              can edit; everyone else sees the assignment read-only. */}
          <Field label="School / campus">
            {canEditSchool ? (
              <select
                value={draft.school_id || ""}
                onChange={(e) => update("school_id", e.target.value)}
                data-testid="demographics-school-id"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                <option value="">No school/campus assigned</option>
                {schoolOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            ) : (
              <div
                data-testid="demographics-school-readonly"
                className="h-9 text-sm border border-[#E5E3DB] rounded-lg px-2 bg-[#F7F5EF] text-[#1F2937] flex items-center"
              >
                {draft.campus || "No school/campus assigned"}
              </div>
            )}
          </Field>
          {/* FP3-097 — Recommended layout: Grade · School · Attendance,
              then Teacher name · Teacher email · Service Status. The
              teacher pair edits the CANONICAL student fields
              (`teacher` / `teacher_email`) shared with Admin. */}
          <Field label="Attendance pattern">
            <select
              value={draft.attendance_pattern}
              onChange={(e) => update("attendance_pattern", e.target.value)}
              data-testid="demographics-attendance"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              {nativeOptions(ATTENDANCE_OPTIONS, (o) => ({ value: o.value, label: o.label, key: o.value }))}
            </select>
          </Field>
          <Field label="Teacher name">
            <Input
              value={draft.teacher}
              onChange={(e) => update("teacher", e.target.value)}
              placeholder="e.g. Maria Lopez"
              className="h-9 text-sm"
              data-testid="demographics-teacher-name"
            />
            <p className="text-[10px] text-[#9B9890] italic mt-1">
              Auto-suggested from the email when blank; your entry is preserved.
            </p>
          </Field>
          <Field label="Teacher email">
            <Input
              type="email"
              value={draft.teacher_email}
              onChange={(e) => {
                const email = e.target.value;
                const prevEmail = draft.teacher_email;
                const prevDerived = deriveTeacherDisplay(prevEmail);
                update("teacher_email", email);
                // Iter 33b / FP3-097 — Auto-derive the teacher name
                // from the email's local part. Respect a hand-edited
                // name: only overwrite when the previous teacher value
                // still matches the previous derivation (i.e. user
                // hasn't customized it). Blank teacher always picks up
                // the derived suggestion.
                if (!draft.teacher || draft.teacher === prevDerived) {
                  update("teacher", deriveTeacherDisplay(email));
                }
              }}
              placeholder="e.g. p.patel@lincoln.k12.us"
              className="h-9 text-sm"
              data-testid="demographics-teacher-email"
            />
            {teacherPairMismatch && (
              <p
                className="text-[10px] text-[#B85C5C] mt-1"
                data-testid="demographics-teacher-mismatch"
              >
                {TEACHER_MISMATCH_MESSAGE}
              </p>
            )}
          </Field>
          {/* FP3-070 follow-up (Jun 8, 2026) — Service Status.
              Single dropdown that drives both `status` (IEP / RTI /
              Monitoring / Graduated) and `is_archived` (Archived).
              Source of truth for Wizard / Health / Builder eligibility. */}
          <Field label="Service Status">
            <select
              value={draft.service_status}
              onChange={(e) => update("service_status", e.target.value)}
              data-testid="demographics-service-status"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              {SERVICE_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
              <p className="text-[10px] text-[#9B9890] italic">
                Active Therapy = IEP + RTI. Monitoring, Graduated, and Archived
                are excluded from Wizard, Health, and Builder scheduling.
              </p>
              {/* FP3-072 — Contextual deep-link to the Help Center
                  Service Status article. */}
              <HelpLink
                articleId="set-service-status"
                testId="demographics-service-status-help"
              />
            </div>
          </Field>
          {/* FP3-045 — Student identity fields. Both optional;
              normalized server-side (student_email lowercased + trim).
              Surface here so providers can complete profile data
              they pick up from school records. */}
          <Field label="External Student ID">
            <Input
              value={draft.external_id || ""}
              onChange={(e) => update("external_id", e.target.value)}
              data-testid="demographics-external-id"
              placeholder="e.g. SIS-12345"
              className="h-9 text-sm"
            />
            <p className="text-[10px] text-[#9B9890] italic mt-1">
              Optional. Use the school or district&apos;s local student ID if available.
            </p>
          </Field>
          <Field label="Student Email">
            <Input
              type="email"
              value={draft.student_email || ""}
              onChange={(e) => update("student_email", e.target.value)}
              data-testid="demographics-student-email"
              placeholder="student@school.edu"
              className="h-9 text-sm"
            />
            <p className="text-[10px] text-[#9B9890] italic mt-1">
              Optional. Used by some schools as a student identifier and for future integrations.
            </p>
          </Field>
          {/* FP3-035B correction — Platform-wide student demographics.
              DOB powers every age-relative workflow (assessment,
              eligibility, progress reporting). Sex is the broader
              demographic and includes 'other' / 'not specified'.
              FP3-080 — Sex is also the source of truth for speech
              sound norm comparisons (the separate Speech Norm
              Category field was removed). */}
          <Field label="Date of birth">
            <Input
              type="date"
              value={draft.date_of_birth || ""}
              onChange={(e) => update("date_of_birth", e.target.value)}
              data-testid="demographics-dob"
              className="h-9 text-sm"
            />
            {draft.date_of_birth && (
              <p
                className="text-[10px] text-[#6B7280] mt-1"
                data-testid="demographics-dob-age"
              >
                Age today: <span className="text-[#1F2937] font-medium">
                  {computeAgeLabel(draft.date_of_birth)?.label ?? "—"}
                </span> (years;months)
              </p>
            )}
          </Field>
          <Field label="Sex">
            <select
              value={draft.sex || ""}
              onChange={(e) => update("sex", e.target.value)}
              data-testid="demographics-sex"
              className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
            >
              {SEX_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#9B9890] italic mt-1">
              Student demographic. Used for general reporting, trends, and
              speech sound norm comparisons when a reference uses
              female/male norm tables.
            </p>
          </Field>
        </CardContent>
      </Card>

      {/* FP3-035B-1 follow-up — Special Education Profile card.
          Carries Eligibility / LRE / IEP & MET dates. Full width so
          the multi-eligibility editor and SLD/DD/PSD subcategory
          chips display cleanly without horizontal overflow. */}
      <Card
        className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
        data-testid="special-ed-profile-card"
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
            Special Education Profile
          </CardTitle>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Eligibility categories, LRE level, and IEP/MET timelines for this
            student. Saves with the rest of the profile.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="LRE (Least Restrictive Environment)">
              <select
                value={draft.lre || ""}
                onChange={(e) => update("lre", e.target.value)}
                data-testid="demographics-lre"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                <option value="">Not specified</option>
                {lreLevels.map((lvl) => (
                  <option key={lvl.key} value={lvl.key}>{lvl.label}</option>
                ))}
              </select>
            </Field>
            <Field label="IEP due date">
              <Input
                type="date"
                value={draft.iep_due_date || ""}
                onChange={(e) => update("iep_due_date", e.target.value)}
                data-testid="demographics-iep-date"
                className="h-9 text-sm"
              />
            </Field>
            <Field label="MET / evaluation due date">
              <Input
                type="date"
                value={draft.met_due_date || ""}
                onChange={(e) => update("met_due_date", e.target.value)}
                data-testid="demographics-met-date"
                className="h-9 text-sm"
              />
            </Field>
          </div>
          {/* FP3-035B-1 correction — Multi-eligibility editor with
              subcategory checkboxes for SLD / DD / PSD. */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
              Eligibility categories
            </p>
            <EligibilityEditor
              value={draft.eligibility_categories}
              onChange={(next) => update("eligibility_categories", next)}
              taxonomy={eligibilityCategories}
              testIdPrefix="demographics-elig"
            />
            <p className="text-[10px] text-[#9B9890] italic mt-1">
              Up to 4 entries. Primary required when any eligibility is set.
              SLD, DD, and PSD reveal subcategory chips.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Service Commitments */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
              Service Commitments
            </CardTitle>
            <p className="text-[11px] text-[#6B7280] mt-1">
              Primary commitment = total minutes per week or month. Delivery pattern below
              describes how those minutes are typically broken into sessions.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addObligation}
            data-testid="demographics-add-obligation"
            className="rounded-lg h-7 text-[11px] border-[#d4d2cc]"
          >
            <Plus size={11} className="mr-1" />
            Add commitment
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {draft.service_obligations.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">
              No commitments on record. Add a minutes-per-week or minutes-per-month entry to drive Schedule Wizard.
            </p>
          ) : (
            draft.service_obligations.map((o, idx) => (
              <ObligationCard
                key={idx}
                idx={idx}
                obligation={o}
                serviceTypes={serviceTypes}
                onChange={(partial) => updateObligation(idx, partial)}
                onRemove={() => removeObligation(idx)}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Student Constraints — Iter 33b
          FP3-075D — Hidden in Simple Mode (advanced scheduling
          configuration; not part of the calm Simple Mode workflow).
          The route + data + endpoint stay untouched. */}
      {!isSimpleMode && <StudentConstraintsCard studentId={student?.id} />}

      {/* FP3-034D — Provider-block-aware minute calculation toggle.
          FP3-075D — Hidden in Simple Mode AND when Fidelity Monitoring
          is Off, since both contexts strip the surrounding service-
          minute language. The card-level "Apply provider block
          schedule and school calendar exceptions" toggle is the
          advanced-mode-only equivalent of the Builder validation
          pill we already suppress. */}
      {!isSimpleMode && !isFidelityOff && (
        <ProviderBlockFollowToggleCard student={student} onSaved={onSaved} />
      )}

      {/* FP3-097 — Teacher name ↔ email mismatch confirmation. Same
          behavior as the Admin Profile tab: warn, allow an explicit
          Save anyway (district aliases / maiden names / initials-only
          / shared classroom addresses are all legitimate). */}
      <AlertDialog open={pendingTeacherMismatch} onOpenChange={setPendingTeacherMismatch}>
        <AlertDialogContent data-testid="demographics-teacher-mismatch-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Teacher name and email may not match</AlertDialogTitle>
            <AlertDialogDescription>
              {TEACHER_MISMATCH_MESSAGE} District aliases, maiden-name emails,
              initials-only addresses, and shared classroom inboxes are all
              valid — choose Save anyway if this pair is correct.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="demographics-teacher-mismatch-cancel"
              disabled={saving}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={continueSaveAfterMismatch}
              disabled={saving}
              data-testid="demographics-teacher-mismatch-save-anyway"
              className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FP3-070 follow-up — Exit-active-therapy confirmation dialog.
          Fires when user transitions an IEP/RTI student to
          Monitoring / Graduated / Archived. We do NOT mutate any
          blocks / goals / sessions — just warn the user. */}
      <AlertDialog open={pendingExitWarning} onOpenChange={setPendingExitWarning}>
        <AlertDialogContent data-testid="demographics-status-exit-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Move student out of Active Therapy?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;re changing this student&apos;s Service Status from Active
              Therapy (IEP / RTI) to <span className="font-semibold text-[#1F2937]">
              {SERVICE_STATUS_OPTIONS.find((o) => o.value === draft.service_status)?.label}
              </span>. Existing schedule blocks, goals, sessions, and history are
              preserved, but any active recurring blocks for this student may
              need review or removal. The student will no longer appear in the
              Schedule Wizard, Health, or Builder scheduling obligations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="demographics-status-exit-cancel"
              disabled={saving}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={save}
              disabled={saving}
              data-testid="demographics-status-exit-confirm-btn"
              className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              {saving ? "Saving…" : "Continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ObligationCard({ idx, obligation, serviceTypes, onChange, onRemove }) {
  const isNoService = obligation.kind === "no_direct_service";
  const periodLabel = obligation.kind === "minutes_per_month" ? "month" : "week";
  const deliveryTotal =
    (parseInt(obligation.delivery?.sessions_per_period, 10) || 0) *
    (parseInt(obligation.delivery?.minutes_per_session, 10) || 0);
  const matchesTotal = deliveryTotal === (parseInt(obligation.total_minutes, 10) || 0);
  // FP3-035B-1 follow-up — Multi-area focus selector. A commitment
  // represents a single block of minutes; the same block can target
  // multiple focus/service areas (e.g. "60 min/wk, 2×30, covers
  // articulation + language + pragmatics"). Minutes ARE NOT
  // multiplied by area count.
  const selectedTypes = Array.isArray(obligation.service_types)
    ? obligation.service_types
    : (obligation.service_type ? [obligation.service_type] : []);
  const toggleType = (t) => {
    const next = selectedTypes.includes(t)
      ? selectedTypes.filter((x) => x !== t)
      : [...selectedTypes, t];
    // Keep legacy single field synced to the first entry so the rest
    // of the platform (fidelity partitioning, session_type labels) is
    // unaffected.
    onChange({ service_types: next, service_type: next[0] || "" });
  };
  // Union of taxonomy options + any unknown legacy keys already on
  // the obligation so we don't silently drop them.
  const allOptions = (() => {
    const base = serviceTypes || [];
    const extras = selectedTypes.filter((t) => t && !base.includes(t));
    return [...base, ...extras];
  })();

  return (
    <div
      data-testid={`obligation-card-${idx}`}
      className="border border-[#E5E3DB] rounded-xl bg-[#FAFAF7]"
    >
      <div className="px-3 py-2 border-b border-[#ECEAE4] flex items-center justify-between gap-3">
        <span className="text-[10px] uppercase tracking-wider text-[#5C7088] shrink-0">
          Commitment {idx + 1}
        </span>
        <div className="flex-1 flex flex-wrap items-center gap-1">
          {selectedTypes.length === 0 ? (
            <span className="text-[10px] italic text-[#9B9890]">
              No focus areas selected — pick below
            </span>
          ) : (
            selectedTypes.map((t) => (
              <span
                key={`sel-${t}`}
                data-testid={`obligation-${idx}-area-chip-${t}`}
                className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-md bg-[#1E3F33]/10 text-[10px] text-[#1E3F33] border border-[#1E3F33]/20"
              >
                {t}
                <button
                  onClick={() => toggleType(t)}
                  data-testid={`obligation-${idx}-area-remove-${t}`}
                  aria-label={`Remove ${t}`}
                  className="text-[#1E3F33]/60 hover:text-[#1E3F33]"
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <button
          onClick={onRemove}
          data-testid={`obligation-${idx}-remove`}
          className="text-[#6B7280] hover:text-[#B85C5C] p-1 shrink-0"
        >
          <Trash size={12} />
        </button>
      </div>
      <div className="px-3 pt-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
          Focus areas addressed during this commitment
        </p>
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={`obligation-${idx}-area-picker`}
        >
          {allOptions.map((t) => {
            const on = selectedTypes.includes(t);
            return (
              <button
                key={`pick-${t}`}
                onClick={() => toggleType(t)}
                data-testid={`obligation-${idx}-area-toggle-${t}`}
                aria-pressed={on}
                className={`h-7 px-2 rounded-md text-[11px] border transition-colors ${
                  on
                    ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                    : "bg-white text-[#4B5563] border-[#d4d2cc] hover:border-[#1E3F33]/40"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-[#9B9890] italic mt-1.5">
          Select one or more areas this commitment covers. Minutes are
          shared across all selected areas — they are not multiplied per area.
        </p>
      </div>
      <div className="p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
        {/* Primary minute-based commitment */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
            Primary commitment
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {!isNoService && (
              <>
                <Input
                  type="number"
                  min={0}
                  value={obligation.total_minutes}
                  onChange={(e) =>
                    onChange({ total_minutes: parseInt(e.target.value, 10) || 0 })
                  }
                  data-testid={`obligation-${idx}-total`}
                  className="h-8 text-xs w-20 tabular-nums"
                />
                <span className="text-xs text-[#4B5563]">min /</span>
              </>
            )}
            <select
              value={obligation.kind}
              onChange={(e) => onChange({ kind: e.target.value })}
              data-testid={`obligation-${idx}-kind`}
              className="h-8 text-xs border border-[#d4d2cc] rounded-md px-1.5 bg-white"
            >
              {nativeOptions(KIND_OPTIONS, (k) => ({
                value: k.value,
                key: k.value,
                label:
                  k.value === "minutes_per_week"
                    ? "week"
                    : k.value === "minutes_per_month"
                    ? "month"
                    : "no direct service",
              }))}
            </select>
          </div>
        </div>
        {/* Delivery sub-pattern (hidden for no-direct-service) */}
        {!isNoService ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
              Delivery pattern (per {periodLabel})
            </p>
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                min={0}
                value={obligation.delivery?.sessions_per_period || 0}
                onChange={(e) =>
                  onChange({
                    delivery: {
                      ...obligation.delivery,
                      sessions_per_period: parseInt(e.target.value, 10) || 0,
                    },
                  })
                }
                data-testid={`obligation-${idx}-sessions`}
                className="h-8 text-xs w-16 tabular-nums"
              />
              <span className="text-xs text-[#4B5563]">×</span>
              <Input
                type="number"
                min={0}
                value={obligation.delivery?.minutes_per_session || 0}
                onChange={(e) =>
                  onChange({
                    delivery: {
                      ...obligation.delivery,
                      minutes_per_session: parseInt(e.target.value, 10) || 0,
                    },
                  })
                }
                data-testid={`obligation-${idx}-min-per-session`}
                className="h-8 text-xs w-16 tabular-nums"
              />
              <span className="text-xs text-[#4B5563]">min</span>
            </div>
            <p
              className={`text-[10px] mt-1 ${
                matchesTotal ? "text-[#9B9890]" : "text-[#D9A05B]"
              }`}
              data-testid={`obligation-${idx}-balance`}
            >
              {matchesTotal
                ? `Delivery totals ${deliveryTotal} min/${periodLabel} ✓`
                : `Delivery totals ${deliveryTotal} min — does not match primary ${
                    obligation.total_minutes || 0
                  } min/${periodLabel}`}
            </p>
          </div>
        ) : (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
              Delivery
            </p>
            <p className="text-xs text-[#6B7280] italic h-8 flex items-center">
              No direct minutes — wizard skips placement for this commitment.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
        {label}
      </p>
      {children}
    </div>
  );
}

function toFormState(s) {
  if (!s) {
    return {
      teacher: "",
      teacher_email: "",
      grade: "0",
      attendance_pattern: "full_day",
      date_of_birth: "",
      sex: "",
      eligibility_category: "",
      eligibility_categories: [],
      lre: "",
      iep_due_date: "",
      met_due_date: "",
      service_obligations: [],
      // FP3-045 — Student identity defaults.
      external_id: "",
      student_email: "",
      // FP3-064 — Canonical school/campus assignment.
      school_id: "",
      campus: "",
    };
  }
  // FP3-035B-1 correction — Hydrate the multi-eligibility array from
  // the server. Prefer `eligibility_categories` (array, new shape);
  // fall back to synthesizing a single primary entry from the legacy
  // `eligibility_category` string when only that is present so
  // existing records still render in the editor without a migration.
  const rawArr = Array.isArray(s.eligibility_categories) ? s.eligibility_categories : [];
  const hydratedArr = rawArr
    .filter((e) => e && (e.category || e.key))
    .map((e, i) => ({
      category: e.category || e.key || "",
      designation: (e.designation || (i === 0 ? "primary" : i === 1 ? "secondary" : "additional")),
      subcategories: Array.isArray(e.subcategories) ? [...e.subcategories] : [],
    }));
  let eligArr = hydratedArr;
  if (eligArr.length === 0 && s.eligibility_category) {
    eligArr = [{ category: s.eligibility_category, designation: "primary", subcategories: [] }];
  }
  return {
    teacher: s.teacher || "",
    teacher_email: s.teacher_email || "",
    // FP3-035B correction — Migrate legacy "K" → "0" on form open
    // so even un-migrated drafts display as kindergarten=0.
    grade: s.grade === "K" ? "0" : (s.grade || "0"),
    // FP3-092 — normalize legacy aliases (half_day_am → am etc.) so
    // Admin-written legacy tokens render correctly here and persist
    // canonically on the next save.
    attendance_pattern: normalizeAttendancePattern(s.attendance_pattern) || "full_day",
    // FP3-070 follow-up — Service Status / Therapy Status. UI value
    // collapses `{status, is_archived}` into a single dropdown.
    service_status: toServiceStatusUi(s),
    date_of_birth: s.date_of_birth || "",
    sex: normalizeSex(s.sex),
    eligibility_category: s.eligibility_category || "",
    eligibility_categories: eligArr,
    lre: s.lre || "",
    iep_due_date: s.iep_due_date || "",
    met_due_date: s.met_due_date || "",
    service_obligations: (s.service_obligations || []).map((o) => normalizeObligation(o)),
    // FP3-045 — Identity fields seed straight from the student doc.
    external_id: s.external_id || "",
    student_email: s.student_email || "",
    // FP3-064 — Hydrate from canonical school_id + legacy campus
    // display string. The new picker writes school_id and the
    // server syncs campus on save.
    school_id: s.school_id || "",
    campus: s.campus || "",
  };
}

// FP3-035B — Age in years;months for any DOB-relative display. Used
// next to the DOB input and on the student header.
export function computeAgeLabel(dobIso, onDateIso) {
  if (!dobIso || typeof dobIso !== "string" || dobIso.length < 10) return null;
  const [by, bm, bd] = dobIso.slice(0, 10).split("-").map((n) => parseInt(n, 10));
  if (!by || !bm || !bd) return null;
  let ny, nm, nd;
  if (onDateIso && typeof onDateIso === "string" && onDateIso.length >= 10) {
    [ny, nm, nd] = onDateIso.slice(0, 10).split("-").map((n) => parseInt(n, 10));
  } else {
    const now = new Date();
    ny = now.getFullYear(); nm = now.getMonth() + 1; nd = now.getDate();
  }
  let years = ny - by;
  let months = nm - bm;
  if (nd < bd) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  if (years < 0) return null;
  return { years, months, label: `${years};${months}` };
}

// ──────────────────────────────────────────────────────────────
// Student Constraints — Iter 33b
// Optional per-student scheduling constraints (OT, counseling,
// intervention, etc). Behave as hard blocks for THIS student in the
// wizard + schedule health check. Optional date range so temporary
// blocks (e.g. "OT for the next 6 weeks") expire automatically.
// ──────────────────────────────────────────────────────────────
const SC_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

// FP3-107 — Render a canonical `days` array as either "Mon–Fri" (when
// all five weekdays are selected), or a comma-joined list ("Mon, Wed,
// Fri"). Falls back to "—" when the array is empty.
function formatConstraintDays(days) {
  if (!Array.isArray(days) || !days.length) return "—";
  const cleaned = Array.from(new Set(days.filter((d) => d >= 0 && d <= 4))).sort(
    (a, b) => a - b,
  );
  if (!cleaned.length) return "—";
  if (cleaned.length === 5) return "Mon–Fri";
  return cleaned.map((d) => SC_DAY_LABELS[d]).join(", ");
}

// Normalise a constraint row loaded from the API so downstream UI can
// rely on `days` always being present, even on legacy single-day rows.
function normaliseConstraintDays(sc) {
  const raw = Array.isArray(sc?.days) ? sc.days : [];
  const legacy =
    typeof sc?.day_of_week === "number" ? [sc.day_of_week] : [];
  const merged = raw.length ? raw : legacy;
  return Array.from(new Set(merged.filter((d) => d >= 0 && d <= 4))).sort(
    (a, b) => a - b,
  );
}
const SC_KIND_OPTIONS = [
  { value: "OT", label: "OT" },
  { value: "PT", label: "PT" },
  { value: "counseling", label: "Counseling" },
  { value: "intervention", label: "Intervention" },
  { value: "medical", label: "Medical" },
  { value: "other", label: "Other" },
];

function StudentConstraintsCard({ studentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const r = await api.listStudentConstraints({ student_id: studentId });
      setItems(r.data);
    } catch (e) {
      toast.error(`Failed to load student constraints: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  return (
    <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="student-constraints-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
            Student Constraints
            <span className="ml-2 text-[10px] font-normal text-[#6B7280]">({items.length})</span>
          </CardTitle>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Optional. Use for student-specific blocks like OT, counseling, intervention
            time. Wizard treats these as hard blocks for this student.
          </p>
        </div>
        {!adding && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            data-testid="student-constraint-add"
            className="rounded-lg h-7 text-[11px] border-[#d4d2cc]"
          >
            <Plus size={11} className="mr-1" />
            Add constraint
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <p className="text-xs text-[#6B7280]">Loading…</p>
        ) : items.length === 0 && !adding ? (
          <p className="text-[11px] text-[#6B7280] italic">
            No student-specific constraints. Add one if this student has a recurring
            block (OT, counseling, intervention) that conflicts with your scheduling.
          </p>
        ) : (
          <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
            <div className="grid grid-cols-[90px_60px_120px_1fr_180px_60px] gap-2 px-3 py-2 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
              <span>Kind</span>
              <span>Day</span>
              <span>Time</span>
              <span>Label</span>
              <span>Date range</span>
              <span></span>
            </div>
            <div className="divide-y divide-[#ECEAE4]">
              {items.map((sc) => (
                <StudentConstraintRow key={sc.id} sc={sc} onSaved={load} />
              ))}
            </div>
          </div>
        )}
        {adding && (
          <StudentConstraintEditor
            studentId={studentId}
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              load();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function StudentConstraintRow({ sc, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteStudentConstraint(sc.id);
      toast.success("Deleted");
      setConfirmingDelete(false);
      onSaved();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };
  if (editing) {
    return (
      <div className="px-3 py-2 bg-[#FAFAF7]">
        <StudentConstraintEditor
          studentId={sc.student_id}
          existing={sc}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
        />
      </div>
    );
  }
  const days = normaliseConstraintDays(sc);
  const dayLabel = formatConstraintDays(days);
  const range =
    sc.start_date || sc.end_date
      ? `${sc.start_date || "…"} → ${sc.end_date || "ongoing"}`
      : "ongoing";
  return (
    <>
      <div
        data-testid={`student-constraint-row-${sc.id}`}
        className="grid grid-cols-[90px_110px_120px_1fr_180px_60px] gap-2 px-3 py-2 items-center text-[11px]"
      >
        <span className="text-[#5C7088] uppercase tracking-wider text-[10px]">{sc.kind}</span>
        <span
          className="text-[#1F2937]"
          data-testid={`student-constraint-row-${sc.id}-days`}
        >
          {dayLabel}
        </span>
        <span className="text-[#4B5563] tabular-nums">
          {sc.start_time}–{sc.end_time}
        </span>
        <span className="text-[#1F2937] truncate">{sc.label || "—"}</span>
        <span className="text-[10px] text-[#6B7280] tabular-nums">{range}</span>
        <div className="flex justify-end gap-1">
          <button
            onClick={() => setEditing(true)}
            data-testid={`student-constraint-edit-${sc.id}`}
            className="text-[#6B7280] hover:text-[#1E3F33] p-1"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={() => setConfirmingDelete(true)}
            data-testid={`student-constraint-delete-${sc.id}`}
            className="text-[#6B7280] hover:text-[#B85C5C] p-1"
          >
            <Trash size={11} />
          </button>
        </div>
      </div>
      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent data-testid="student-constraint-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete student constraint?</AlertDialogTitle>
            <AlertDialogDescription>
              This will allow scheduling during {dayLabel} {sc.start_time}–{sc.end_time}{" "}
              ({sc.label || sc.kind}). Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="student-constraint-delete-cancel"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={deleting}
              data-testid="student-constraint-delete-confirm"
              className="bg-[#B85C5C] hover:bg-[#9b4747] text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function StudentConstraintEditor({ studentId, existing, onCancel, onSaved }) {
  // FP3-107 — Multi-day support. Editor state now carries a `days`
  // array (canonical). Legacy `day_of_week` on existing rows is
  // normalised into that array on open.
  const [draft, setDraft] = useState(() => {
    if (existing) {
      const days = normaliseConstraintDays(existing);
      return {
        ...existing,
        days: days.length ? days : [1],
      };
    }
    return {
      student_id: studentId,
      kind: "OT",
      // Default to Tue for continuity with the pre-FP3-107 form.
      days: [1],
      start_time: "09:00",
      end_time: "09:30",
      label: "",
      start_date: "",
      end_date: "",
    };
  });
  const [saving, setSaving] = useState(false);
  const toggleDay = (d) => {
    setDraft((cur) => {
      const set = new Set(cur.days || []);
      if (set.has(d)) set.delete(d);
      else set.add(d);
      return { ...cur, days: Array.from(set).sort((a, b) => a - b) };
    });
  };
  const canSave =
    !saving &&
    Array.isArray(draft.days) &&
    draft.days.length > 0 &&
    draft.start_time &&
    draft.end_time &&
    draft.end_time > draft.start_time &&
    (!draft.start_date || !draft.end_date || draft.end_date >= draft.start_date);
  const save = async () => {
    if (!canSave) {
      // Provide a specific hint so the "Save disabled" state isn't
      // silent — spec §7 requires that selected weekdays are never
      // discarded silently.
      if (!draft.days?.length) {
        toast.error("Pick at least one weekday.");
      } else if (draft.end_time <= draft.start_time) {
        toast.error("End time must be after start time.");
      } else if (
        draft.start_date &&
        draft.end_date &&
        draft.end_date < draft.start_date
      ) {
        toast.error("End date cannot be before start date.");
      }
      return;
    }
    setSaving(true);
    try {
      const canonicalDays = Array.from(
        new Set((draft.days || []).filter((d) => d >= 0 && d <= 4)),
      ).sort((a, b) => a - b);
      const payload = {
        student_id: studentId,
        kind: draft.kind,
        // Canonical multi-day field. Backend mirrors it into
        // `day_of_week` (first day) for legacy readers.
        days: canonicalDays,
        // Explicit legacy mirror so any pre-FP3-107 backend still
        // receives a valid `day_of_week` — remove once every reader
        // is on the new shape.
        day_of_week: canonicalDays[0],
        start_time: draft.start_time,
        end_time: draft.end_time,
        label: draft.label || "",
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
      };
      if (existing?.id) {
        await api.updateStudentConstraint(existing.id, payload);
      } else {
        await api.createStudentConstraint(payload);
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      data-testid="student-constraint-editor"
      className="border border-[#E5E3DB] rounded-lg p-3 bg-[#FAFAF7] space-y-2"
    >
      <div className="grid grid-cols-1 md:grid-cols-[110px_260px_260px_1fr] gap-2 items-center">
        <select
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
          data-testid="student-constraint-kind"
          className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
        >
          {nativeOptions(SC_KIND_OPTIONS, (o) => ({ value: o.value, label: o.label, key: o.value }))}
        </select>
        {/* FP3-107 — Multi-day chip selector. Compact, keyboard-
            operable, and the current active state is visually
            unambiguous. */}
        <div
          className="flex items-center gap-1"
          data-testid="student-constraint-days"
          role="group"
          aria-label="Select weekdays"
        >
          {SC_DAY_LABELS.map((lbl, idx) => {
            const active = (draft.days || []).includes(idx);
            return (
              <button
                key={lbl}
                type="button"
                onClick={() => toggleDay(idx)}
                aria-pressed={active}
                data-testid={`student-constraint-day-${lbl.toLowerCase()}`}
                data-active={active ? "true" : "false"}
                className={
                  "h-8 min-w-[36px] px-2 text-[11px] rounded-md border transition-colors " +
                  (active
                    ? "bg-[#1E3F33] text-white border-[#1E3F33] hover:bg-[#173127]"
                    : "bg-white text-[#374151] border-[#d4d2cc] hover:bg-[#F5F3EE]")
                }
              >
                {lbl}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="time"
            value={draft.start_time}
            onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
            data-testid="student-constraint-start"
            className="h-8 text-xs w-[120px]"
          />
          <span className="text-[10px] text-[#6B7280]">–</span>
          <Input
            type="time"
            value={draft.end_time}
            onChange={(e) => setDraft({ ...draft, end_time: e.target.value })}
            data-testid="student-constraint-end"
            className="h-8 text-xs w-[120px]"
          />
        </div>
        <Input
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          placeholder="Label (e.g. OT pull-out)"
          data-testid="student-constraint-label"
          className="h-8 text-xs"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[140px_140px_1fr_auto] gap-2 items-center">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-0.5">
            Start date (optional)
          </p>
          <Input
            type="date"
            value={draft.start_date || ""}
            onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
            data-testid="student-constraint-start-date"
            className="h-8 text-xs"
          />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-0.5">
            End date (optional)
          </p>
          <Input
            type="date"
            value={draft.end_date || ""}
            onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
            data-testid="student-constraint-end-date"
            className="h-8 text-xs"
          />
        </div>
        <div className="text-[10px] text-[#9B9890] italic">
          Leave dates blank for an ongoing constraint.
        </div>
        <div className="flex items-center gap-1 justify-end">
          <Button
            size="sm"
            onClick={save}
            disabled={!canSave}
            data-testid="student-constraint-save"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-md h-8 text-[11px] px-3 disabled:opacity-40"
          >
            <FloppyDisk size={11} weight="bold" className="mr-1" />
            {saving ? "…" : "Save"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            data-testid="student-constraint-cancel"
            className="rounded-md h-8 text-[11px] px-3 border-[#d4d2cc]"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}




// ──────────────────────────────────────────────────────────────
// FP3-034D — Follow Provider Block Schedule (calculation toggle)
//
// When enabled, the student's recurring scheduled blocks are NOT
// counted on weeks the assigned provider has a "personal" block
// covering them (testing weeks, admin weeks, every-4th-week blocks,
// all-day blocks). This is purely a CALCULATION fix for monthly
// service-minute reconciliation — the Schedule Builder still places
// the weekly slot as-is, no sessions are mass-suppressed, no data is
// reshaped.
// ──────────────────────────────────────────────────────────────
function ProviderBlockFollowToggleCard({ student, onSaved }) {
  const [saving, setSaving] = useState(false);
  const value = !!student?.follow_provider_block_schedule;

  const setValue = async (next) => {
    if (!student?.id) return;
    setSaving(true);
    try {
      await api.updateStudentDemographics(student.id, {
        follow_provider_block_schedule: next,
      });
      toast.success(
        next
          ? "This student now follows the provider block schedule for minute calculations."
          : "Reverted to standard 4-weeks-per-month calculation."
      );
      await onSaved?.();
    } catch (e) {
      toast.error(
        `Failed to update setting: ${e?.response?.data?.detail || e.message}`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
      data-testid="provider-block-follow-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
          Service-Minute Calculation
        </CardTitle>
        <p className="text-[11px] text-[#6B7280] mt-1">
          By default, monthly service-minute calculations assume a 30-minute
          weekly slot occurs 4 times per month (=120 min/month). For students
          whose provider has recurring all-day or every-Nth-week blocks
          (testing weeks, admin weeks, no-service weeks) — or whose school
          has calendar exceptions (holidays, breaks, closures) — that math
          is inflated. Toggle this on so the Current Schedule reconciliation
          excludes those weeks and matches the real planned cadence.
        </p>
      </CardHeader>
      <CardContent className="pt-1">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value}
            disabled={saving}
            onChange={(e) => setValue(e.target.checked)}
            data-testid="follow-provider-block-toggle"
            className="mt-0.5 accent-[#1E3F33] w-4 h-4"
          />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#1F2937]">
              Apply provider block schedule and school calendar exceptions
              to service-minute calculations
            </div>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Excludes occurrences during all-day, recurring, and
              every-Nth-week provider blocks (testing weeks, admin weeks,
              no-service blocks) AND school calendar exceptions (holidays,
              breaks, closures, no-school days) from the projected monthly
              minutes total. Excluded weeks are not counted as missed
              sessions or service deficits.
            </p>
            {value && (
              <p
                className="text-[11px] text-[#1E3F33] mt-1.5 font-medium"
                data-testid="follow-provider-block-status"
              >
                Active — minute calculations now exclude provider blocks
                and school calendar exceptions.
              </p>
            )}
          </div>
        </label>
      </CardContent>
    </Card>
  );
}
